import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Typography, IconButton, Box, Tooltip } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { FormsySelect } from 'formsy-material-ui/lib';
import { RadioButton, RadioButtonGroup } from 'material-ui/RadioButton';
import { Prompt } from 'react-router-dom';
import MenuItem from 'material-ui/MenuItem';
import RaisedButton from 'material-ui/RaisedButton';
import Formsy from 'formsy-react';
import InnerContent from '../../components/PageStructure/InnerContent/InnerContent';
import { getDateForApi, extractDateOnly, isValidDate } from '../../utils/dates';
import AlertBox from '../../components/common/AlertBox/AlertBox';
import ConfirmationDialog from '../../components/common/ConfirmationDialog/ConfirmationDialog';
import PostcodeSearch from '../../containers/common/PostcodeSearch/PostcodeSearchContainer';

import PageContent from '../../components/PageStructure/PageContent/PageContent';
import PageHeader from '../../components/PageStructure/PageHeader/PageHeader';
import ApiSelector from '../../containers/common/selectors/selectorContainer';
import { rosteredCliniciansEndpoint, teamsEndpoint } from '../../settings/constants';

import globals from '../../globals/communityGlobals';
import { formatNhs, doubleActionPrevent } from '../../utils/helpers';
import { common, dropDownStyle } from '../../theme/AllocateBaseTheme';
import VisitActualActivitiesTable from '../../components/VisitActuals/Form/VisitActualActivitiesTable';
import KeyboardDateSelector from '../../components/common/KeyboardDateTimeSelectors/KeyboardDateSelector';
import KeyboardTimeSelector from '../../components/common/KeyboardDateTimeSelectors/KeyboardTimeSelector';
import {
  getErrorProps,
  combineDateTime,
  checkRequiredOnBlur,
  checkEndTimeAfterNow,
} from '../../utils/keyboardSelectorFunctions';

const locale = globals.getArea('treatments');
const errors = globals.getArea('error');

const styles = {
  radioButton: { marginBottom: '5px' },
  radioButtonGroup: { flexDirection: 'column', width: '250px', marginLeft: 0 },
};
export const selectionTypes = {
  onSite: 'OnSite',
  phoneCall: 'PhoneCall',
};

class TreatmentCreateRetroVisit extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      startTime: null,
      endTime: null,
      dateOfVisit: new Date(globals.now),
      clinicianId: null,
      orgUnitId: null,
      canSubmit: false,
      activities: [],
      unSavedChanges: false,
      showError: false,
      selectionType: selectionTypes.onSite,
      clearActivities: false,
      showWarning: false,
      startingPostcode: null,
      showSaveConfirmationDialog: false,
      treatmentDataModel: null,
      validationErrors: {},
    };
  }

  componentWillMount() {
    // preventing navigation from the route
    window.onbeforeunload = () => this.state.unSavedChanges;
  }

  componentDidMount = () => {
    const { match, getReferral } = this.props;
    getReferral(match.params.id);
    this.setState({ showWarning: this.props.referral && this.props.referral.referralService == null });
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevState.dateOfVisit !== this.state.dateOfVisit) {
      const { startTime, endTime } = this.state;

      if (!startTime || !endTime) return;

      const errorMsg = errors.getMsg('cannotBeInTheFuture');
      const startAt = combineDateTime(this.state.dateOfVisit, startTime);
      const endAt = combineDateTime(this.state.dateOfVisit, endTime);

      const validateTime = (time, field) => {
        const isValid = !checkEndTimeAfterNow(time);
        this.setState(oldState => ({
          validationErrors: {
            ...oldState.validationErrors,
            [field]: isValid ? errorMsg : null,
          },
        }));
      };

      validateTime(startAt, 'startTime');
      validateTime(endAt, 'endTime');
    }
  }

  componentWillUnmount() {
    this.setState({
      unSavedChanges: false,
      showError: false,
      showWarning: false,
    });

    window.onbeforeunload = null;
  }

  setValidationErrors = newValidationErrors => {
    this.setState({
      validationErrors: newValidationErrors,
    });
  };

  handleDateChange = (fieldName, date) => {
    let errorMsg = null;

    if (!date) errorMsg = errors.getMsg('requiredFieldValidationMessage');
    else date = extractDateOnly(date);

    this.setValidationErrors({ ...this.state.validationErrors, [fieldName]: errorMsg });
    this.setState({ [fieldName]: date });
  };

  handleTimeChange = (fieldName, time) => {
    let errorMsg = null;
    if (!time) errorMsg = errors.getMsg('requiredFieldValidationMessage');
    else {
      if (isValidDate(this.state.dateOfVisit)) {
        time = combineDateTime(this.state.dateOfVisit, time);
      }
      if (this.state.dateOfVisit && !checkEndTimeAfterNow(time)) {
        errorMsg = errors.getMsg('cannotBeInTheFuture');
      }
    }
    this.setValidationErrors({ ...this.state.validationErrors, [fieldName]: errorMsg });
    this.setState({ [fieldName]: time });
  };

  get startAt() {
    return combineDateTime(this.state.dateOfVisit, this.state.startTime);
  }

  get endAt() {
    return combineDateTime(this.state.dateOfVisit, this.state.endTime);
  }

  subtractDates = (date1, date2) => {
    const { dateOfVisit } = this.state;

    if (dateOfVisit && date1) date1 = combineDateTime(dateOfVisit, date1);
    if (dateOfVisit && date2) date2 = combineDateTime(dateOfVisit, date2);

    if (date1 !== null && date2 !== null) {
      if (date1 > date2) {
        date2 = moment(date2).add(1, 'days');
      }
      return moment.duration(Math.abs(moment(date2).diff(moment(date1)))).format('H [hrs]: m [min]');
    }
    return '-';
  };

  handleSelectionTypeSwitch = (event, value) => {
    this.setState({
      selectionType: value,
      activities: [],
      clearActivities: true,
    });
  };

  handleSaveCancel = () => {
    this.setState({
      showSaveConfirmationDialog: false,
      treatmentDataModel: null,
    });
  };

  handleSaveConfirm = () => {
    this.setState({
      showSaveConfirmationDialog: false,
    });
    this.createRetroVisit(this.state.treatmentDataModel);
  };

  save = doubleActionPrevent.bind(this)('Save', async model => {
    this.setState({ unSavedChanges: false });
    const { referral } = this.props;
    const { selectionType, startingPostcode } = this.state;
    const treatmentData = {
      ...model,
      referralId: referral.id,
      startTime: getDateForApi(this.startAt),
      endTime: getDateForApi(this.endAt),
      startDate: getDateForApi(this.state.dateOfVisit),
      visitType: selectionType,
      startingPostcode,
    };

    if (startingPostcode !== null && startingPostcode.length > 0) {
      this.setState({ showSaveConfirmationDialog: true, treatmentDataModel: treatmentData });
    } else {
      this.createRetroVisit(treatmentData);
    }
  });

  createRetroVisit = treatmentData => {
    this.props
      .createRetrospectiveVisit({ treatment: treatmentData })
      .then(() => {
        if (this.props.errorMessage === null) {
          this.props.goBack();
        }
      })
      .catch(() => this.setState({ showError: true }));
  };

  handleClinicianChange = (event, value, payload) => {
    this.setState({ clinicianId: payload });
  };

  handleOrgUnitChange = (event, value, payload) => {
    const filteredActivities =
      this.state.activities &&
      this.state.activities.filter(a => a.orgUnitsIds.length === 0 || a.orgUnitsIds.some(id => id === payload));

    this.setState({ orgUnitId: payload, activities: filteredActivities });
  };

  enableButton = () => {
    this.setState({
      canSubmit: true,
    });
  };

  disableButton = () => {
    this.setState({
      canSubmit: false,
    });
  };

  handleActivitiesReset = () => {
    this.setState({
      clearActivities: false,
    });
  };

  handleAddedActivities = activities => {
    this.setState({
      activities,
    });
  };

  handleChange = () => {
    this.setState({ unSavedChanges: true });
  };

  filterActivitiesByOrgUnit = options => {
    if (this.state.orgUnitId === undefined || this.state.orgUnitId === null) {
      return options;
    }

    return options.filter(o => {
      return o.orgUnitsIds.length === 0 || o.orgUnitsIds.some(id => id === this.state.orgUnitId);
    });
  };

  noValidationErrors = () => {
    const { dateOfVisit, startTime, endTime, validationErrors } = this.state;
    return (
      isValidDate(dateOfVisit) &&
      !validationErrors.dateOfVisit &&
      isValidDate(startTime) &&
      !validationErrors.startTime &&
      isValidDate(endTime) &&
      !validationErrors.endTime
    );
  };

  render() {
    const {
      startTime,
      endTime,
      dateOfVisit,
      clinicianId,
      orgUnitId,
      canSubmit,
      showError,
      selectionType,
      clearActivities,
      showWarning,
      validationErrors,
    } = this.state;
    const { pageTitle, goBack, referral, errorMessage } = this.props;

    const { patient } = referral || {};

    return (
      <PageContent pageTitle={pageTitle}>
        <div className="content-wrapper">
          <PageHeader>
            <Box className="title-section" display="flex" alignItems="center" px={2} py={1}>
              <Box>
                <Tooltip title={locale.getMsg('back')}>
                  <IconButton
                    className="back-btn"
                    aria-label="back"
                    size="small"
                    color="primary"
                    onClick={() => goBack()}
                  >
                    <ArrowBackIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box flexGrow={1}>
                <Typography variant="h3">{locale.getMsg('createCompletedVisitPageHeader')}</Typography>
                <Typography variant="h6">
                  {patient &&
                    locale.getMsg('patientInfoHeader', [patient.patientFullName, formatNhs(patient.nhsNumber)])}
                </Typography>
              </Box>
            </Box>
          </PageHeader>
          <InnerContent isBorderless>
            <div className="full-page">
              <Formsy.Form
                ref={this.combineRefs}
                onChange={this.handleChange}
                onSubmit={this.save}
                onValid={this.enableButton}
                onInvalid={this.disableButton}
              >
                {showWarning && (
                  <AlertBox
                    style={{ color: 'red' }}
                    content={locale.getMsg('missingReferralServiceWarning')}
                    type="notification"
                  />
                )}
                <Prompt when={this.state.unSavedChanges} message={locale.getMsg('messagePopUp')} />
                <Box display="flex" p={1}>
                  <Box p={1} flexGrow={1}>
                    <RadioButtonGroup
                      className="row"
                      style={styles.radioButtonGroup}
                      name="selectionType"
                      defaultSelected={selectionTypes.onSite}
                      onChange={this.handleSelectionTypeSwitch}
                      valueSelected={selectionType}
                    >
                      <RadioButton value={selectionTypes.onSite} label="On Site" style={styles.radioButton} />
                      <RadioButton value={selectionTypes.phoneCall} label="Phone Call" style={styles.radioButton} />
                    </RadioButtonGroup>
                  </Box>
                </Box>
                <Box display="flex" p={1}>
                  <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
                    <KeyboardDateSelector
                      name="startDate"
                      className="col-c3 mui-date-picker"
                      required
                      label={locale.getMsg('date')}
                      value={dateOfVisit}
                      onChange={date => this.handleDateChange('dateOfVisit', date)}
                      onBlur={event =>
                        checkRequiredOnBlur(
                          event.target.value,
                          this.setValidationErrors,
                          'dateOfVisit',
                          validationErrors,
                        )
                      }
                      errorProps={getErrorProps(validationErrors.dateOfVisit)}
                      maxDate={new Date(globals.now)}
                      maxDateMessage={locale.getMsg('dateInThePast')}
                    />
                  </Box>
                  <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
                    <ApiSelector
                      name="orgUnitId"
                      endpoint={teamsEndpoint}
                      reduxIdentifier="teams"
                      required
                      isFetchRequired={isValidDate(dateOfVisit)}
                      onChange={this.handleOrgUnitChange}
                      floatingLabelText={`${locale.getMsg('team')}*`}
                      value={orgUnitId || []}
                      callArgs={{
                        relevantDay: moment(dateOfVisit).format(globals.dateFormats.date),
                      }}
                    />
                  </Box>
                  <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
                    <ApiSelector
                      name="clinicianId"
                      endpoint={rosteredCliniciansEndpoint}
                      reduxIdentifier="rosteredClinicians"
                      required
                      floatingLabelText={`${locale.getMsg('clinicians')}*`}
                      isFetchRequired={isValidDate(dateOfVisit)}
                      onChange={this.handleClinicianChange}
                      disabled={!orgUnitId}
                      callArgs={{
                        orgUnitId,
                        relevantDay: moment(dateOfVisit).format(globals.dateFormats.date),
                      }}
                      value={clinicianId || []}
                    />
                  </Box>
                </Box>
                <Box display="flex" p={1}>
                  <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
                    <FormsySelect
                      name="status"
                      required
                      floatingLabelText={`${locale.getMsg('status')}*`}
                      floatingLabelStyle={common.floatingLabelStyle}
                      floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
                      floatingLabelFocusStyle={common.floatingLabelFocusStyle}
                      underlineStyle={common.underlineStyle}
                      style={dropDownStyle.style}
                      iconStyle={dropDownStyle.iconStyle}
                      labelStyle={dropDownStyle.labelStyle}
                      menuStyle={dropDownStyle.menuStyle}
                      value={4}
                    >
                      <MenuItem value={4} primaryText={locale.getMsg('completed')} />
                      <MenuItem value={5} primaryText={locale.getMsg('cancelled')} />
                    </FormsySelect>
                  </Box>
                  <Box p={1} style={{ width: '33.3%' }}>
                    <KeyboardTimeSelector
                      name="startTime"
                      className="mui-date-picker"
                      required
                      label={locale.getMsg('startTime')}
                      value={startTime}
                      onChange={time => this.handleTimeChange('startTime', time)}
                      onBlur={event =>
                        checkRequiredOnBlur(event.target.value, this.setValidationErrors, 'startTime', validationErrors)
                      }
                      errorProps={getErrorProps(validationErrors.startTime)}
                    />
                  </Box>
                  <Box p={1} style={{ width: '33.3%' }}>
                    <KeyboardTimeSelector
                      name="endTime"
                      className="mui-date-picker"
                      required
                      label={locale.getMsg('endTime')}
                      value={endTime}
                      onChange={time => this.handleTimeChange('endTime', time)}
                      onBlur={event =>
                        checkRequiredOnBlur(event.target.value, this.setValidationErrors, 'endTime', validationErrors)
                      }
                      errorProps={getErrorProps(validationErrors.endTime)}
                    />
                  </Box>
                </Box>
                <Box display="flex" p={1} flexDirection="column">
                  <Box p={1} flexGrow={1}>
                    <strong>{`${locale.getMsg('visitDuration')}: `}</strong>
                    {this.subtractDates(startTime, endTime)}
                  </Box>

                  <Box p={1} flexGrow={1}>
                    <Typography variant="h3" gutterBottom>
                      {locale.getMsg('visitActivities')}
                    </Typography>
                  </Box>
                  <Box p={1} flexGrow={1}>
                    <VisitActualActivitiesTable
                      name="activities"
                      addedActivities={activities => this.handleAddedActivities(activities)}
                      canEdit
                      visitType={selectionType}
                      clearActivities={clearActivities}
                      handleActivitiesReset={this.handleActivitiesReset}
                      filterActivitiesByOrgUnit={options => this.filterActivitiesByOrgUnit(options)}
                      filteredActivitiesByOrgUnit={this.state.activities}
                    />
                  </Box>

                  {selectionType === selectionTypes.onSite && (
                    <Box>
                      <div className="row">
                        <Box p={1}>
                          <Typography variant="h5">Starting Postcode</Typography>
                        </Box>
                        <Box display="flex">
                          <Box p={1} flexGrow={1} style={{ width: '25%' }}>
                            <div
                              onChange={e =>
                                this.setState({
                                  startingPostcode: e.target.value.trim(),
                                })
                              }
                            >
                              <PostcodeSearch
                                showOnlyPostcode
                                onSelect={selectedPostcode => {
                                  this.setState({
                                    startingPostcode: selectedPostcode.trim(),
                                  });
                                }}
                              />
                            </div>
                          </Box>
                        </Box>
                      </div>
                    </Box>
                  )}

                  <Box p={1} flexGrow={1}>
                    {showError && errorMessage && <p style={{ color: 'red' }}>{errors.getMsg(errorMessage)}</p>}
                  </Box>

                  <Box p={1} flexGrow={1}>
                    <RaisedButton
                      primary
                      label="Submit"
                      type="submit"
                      disabled={!canSubmit || this.state.activities.length === 0 || !this.noValidationErrors()}
                    />
                  </Box>
                </Box>
                <ConfirmationDialog
                  open={this.state.showSaveConfirmationDialog}
                  handleCancel={this.handleSaveCancel}
                  handleConfirm={this.handleSaveConfirm}
                  title={'Save Retrospective Visit'}
                  text={locale.getMsg('confirmationDialogStartingPostcodeRetroVisit')}
                />
              </Formsy.Form>
            </div>
          </InnerContent>
        </div>
      </PageContent>
    );
  }
}

TreatmentCreateRetroVisit.defaultProps = {
  errorMessage: '',
  referral: null,
};

TreatmentCreateRetroVisit.propTypes = {
  goBack: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  pageTitle: PropTypes.string.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      referralId: PropTypes.string,
    }),
  }).isRequired,
  getReferral: PropTypes.func.isRequired,
  referral: PropTypes.shape({
    dateReceived: PropTypes.string,
    id: PropTypes.number,
    locationType: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    orgUnits: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    ),
    referenceCode: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    referralPriority: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    referralSource: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  }),
  createRetrospectiveVisit: PropTypes.func.isRequired,
};

export default TreatmentCreateRetroVisit;
